import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useScrollLock } from "../../../hooks/useScrollLock";

import {
  FiPlus,
  FiX,
  FiChevronDown,
  FiEdit,
  FiUpload,
  FiStar,
  FiTrash2,
} from "react-icons/fi";
import {
  AnalyticsInfo,
  AnalyticsStat,
  AnalyticsStats,
  AnalyticsTitle,
  Badge,
  Btn,
  FormSelect,
  PageSection,
  PriceContainer,
  ProductBand,
  ProductCard,
  ProductCategory,
  ProductDiscountPrice,
  ProductGrid,
  ProductHeader,
  ProductImage,
  ProductImageContainer,
  ProductMeta,
  ProductMetaItem,
  ProductPrice,
  ProductShortDesc,
  ProductSizes,
  ProductTitle,
  ProductActions,
  SizeStockBadge,
  SizeStockDisplay,
  SizeStockList,
  SizeStockTitle,
  StockBadge,
  StockInfo,
  TableHeader,
  TableTitle,
  FormGroup,
  FormLabel,
  FormInput,
  FormTextarea,
  LoadingContainer,
  Relative,
  CloseBtn,
} from "./BandProductsPage.styles";

import {
  formatPrice,
  getBadgeLabel,
  getProductSizeStocksWithAlerts,
  getVariant,
} from "../../../utils/utils";

import { selectUser } from "../../../store/slices/auth/slice";
import { getBandProducts } from "../../../store/slices/bandDashboard/asyncThunks";
import {
  editProduct,
  createProduct,
  deleteProduct,
} from "../../../store/slices/products/asyncThunks";

import {
  selectCategories,
  selectProducts,
} from "../../../store/slices/products/slice";
import {
  selectBandAnalytics,
  selectBandProductAnalytics,
  selectBandProducts,
} from "../../../store/slices/bandDashboard/slice";
import { toast } from "../../../components/modern-ui/sonner";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalClose,
  FormActionsContainer,
  Button,
  SizeSelector,
  SizeOption,
  ImageUploadArea,
  ImagePreviewGrid,
  ImagePreviewItem,
  PreviewImage,
  ImageControls,
  ImageControlButton,
  ImageLabel,
  UploadInstructions,
  TotalStockDisplay,
  SizeStockInput,
  SizeStockLabel,
  SizeStockItem,
  SizeStockContainer,
  ModalOverlay,
  ConfirmBox,
  ModalActions,
  Cancel,
  Confirm,
} from "../AdminProductsPage/AdminProductsPage.styles";
import BandLayout from "../BandLayout/BandLayout";
import { FaTimes } from "react-icons/fa";
import { AnimatePresence, motion } from "framer-motion";

const BandProductsPage = () => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const bandAnalytics = useSelector(selectBandAnalytics);
  const bandProductAnalytics = useSelector(selectBandProductAnalytics);
  const bandProducts = useSelector(selectBandProducts);
  const categories = useSelector(selectCategories);
  const { deleteLoading } = useSelector(selectProducts);

  // Product editing state
  const [activeModal, setActiveModal] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productName, setProductName] = useState("");
  const [productDescription, setProductDescription] = useState("");
  const [productPrice, setProductPrice] = useState("");
  const [productSalePrice, setProductSalePrice] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [sizeStocks, setSizeStocks] = useState({});
  const [sizeStockAlerts, setSizeStockAlerts] = useState({});
  const [isNew, setIsNew] = useState(false);
  const [isFeatured, setIsFeatured] = useState(false);
  const [isBestSeller, setIsBestSeller] = useState(false);
  const [isLimited, setIsLimited] = useState(false);
  const [images, setImages] = useState([]);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [deletedImageIds, setDeletedImageIds] = useState([]);

  // Delete modal state
  const [deleteProductModal, setDeleteProductModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  useScrollLock(activeModal || deleteProductModal);

  useEffect(() => {
    dispatch(getBandProducts({ bandId: user.bandId })).unwrap();
  }, []);

  // Delete product
  const handleDeleteProduct = () => {
    if (productToDelete) {
      dispatch(deleteProduct(productToDelete.id))
        .unwrap()
        .then(() => {
          toast.success("Uspešno ste obrisali proizvod.");
          setDeleteProductModal(false);
          setProductToDelete(null);
        })
        .catch((err) => {
          toast.error(err.message);
        });
    }
  };

  const getProductSizes = (product) => {
    const sizes = [];
    if (product.sizeS) sizes.push("S");
    if (product.sizeM) sizes.push("M");
    if (product.sizeL) sizes.push("L");
    if (product.sizeXL) sizes.push("XL");
    if (product.sizeXXL) sizes.push("XXL");
    if (product.size3XL) sizes.push("3XL");
    if (product.size4XL) sizes.push("4XL");
    if (product.size5XL) sizes.push("5XL");
    return sizes;
  };

  // Product modal functions
  const openAddModal = () => {
    setSelectedProduct(null);
    resetForm();
    setActiveModal("addProduct");
  };

  // Reset form
  const resetForm = () => {
    setProductName("");
    setProductDescription("");
    setProductPrice("");
    setProductSalePrice("");
    setSelectedCategory("");
    setSelectedSizes([]);
    setSizeStocks({
      S: 0,
      M: 0,
      L: 0,
      XL: 0,
      XXL: 0,
      "3XL": 0,
      "4XL": 0,
      "5XL": 0,
    });
    setSizeStockAlerts({
      S: 10,
      M: 10,
      L: 10,
      XL: 10,
      XXL: 10,
      "3XL": 10,
      "4XL": 10,
      "5XL": 10,
    });
    setImages([]);
    setMainImageIndex(0);
    setDeletedImageIds([]);
    setIsNew(false);
    setIsFeatured(false);
    setIsBestSeller(false);
    setIsLimited(false);
  };

  const openEditModal = (product) => {
    setSelectedProduct(product);
    setProductName(product.name || "");
    setProductDescription(product.description || "");
    setProductPrice(product.price || "");
    setProductSalePrice(product.salePrice || "");
    setSelectedCategory(product.categoryId || product.category?.id || "");

    // Get available sizes
    const availableSizes = [];
    if (product.sizeS) availableSizes.push("S");
    if (product.sizeM) availableSizes.push("M");
    if (product.sizeL) availableSizes.push("L");
    if (product.sizeXL) availableSizes.push("XL");
    if (product.sizeXXL) availableSizes.push("XXL");
    if (product.size3XL) availableSizes.push("3XL");
    if (product.size4XL) availableSizes.push("4XL");
    if (product.size5XL) availableSizes.push("5XL");
    setSelectedSizes(availableSizes);

    // Set stock po veličinama
    setSizeStocks({
      S: product.stockSizeS || 0,
      M: product.stockSizeM || 0,
      L: product.stockSizeL || 0,
      XL: product.stockSizeXL || 0,
      XXL: product.stockSizeXXL || 0,
      "3XL": product.stockSize3XL || 0,
      "4XL": product.stockSize4XL || 0,
      "5XL": product.stockSize5XL || 0,
    });

    // Set stock alert thresholds
    setSizeStockAlerts({
      S: product.stockAlertS || 10,
      M: product.stockAlertM || 10,
      L: product.stockAlertL || 10,
      XL: product.stockAlertXL || 10,
      XXL: product.stockAlertXXL || 10,
      "3XL": product.stockAlert3XL || 10,
      "4XL": product.stockAlert4XL || 10,
      "5XL": product.stockAlert5XL || 10,
    });

    // Handle existing images
    const existingImages = [];
    if (
      product.productImages &&
      Array.isArray(product.productImages) &&
      product.productImages.length > 0
    ) {
      product.productImages.forEach((image, index) => {
        if (image && image.id && image.url) {
          existingImages.push({
            id: image.id,
            url: image.url,
            isMain: image.isMain,
            isExisting: true,
          });
        }
      });

      const mainIndex = existingImages.findIndex((img) => img.isMain);
      setMainImageIndex(mainIndex >= 0 ? mainIndex : 0);
    }

    setImages(existingImages);
    setDeletedImageIds([]);

    setIsNew(product.isNew || false);
    setIsFeatured(product.isFeatured || false);
    setIsBestSeller(product.isBestSeller || false);
    setIsLimited(product.isLimited || false);
    setActiveModal("editProduct");
  };

  // Modal functions
  const closeModal = () => {
    setActiveModal(null);
    setSelectedProduct(null);
    resetForm();
  };

  // Form submission
  const handleProductSubmit = (e) => {
    e.preventDefault();

    const category = categories.data.find(
      (c) => c.id === parseInt(selectedCategory)
    );
    const productSizes = category?.hasSizes ? selectedSizes : [];

    // Create FormData for file upload support
    const formData = new FormData();

    // Add images and calculate proper mainImageIndex for new images
    const newImages = images.filter((img) => img.isNew);
    let newImageMainIndex = -1;

    newImages.forEach((image, newImageIndex) => {
      formData.append("images", image.file);

      // Find the index of this new image in the original images array
      const originalIndex = images.findIndex((img) => img.id === image.id);
      if (originalIndex === mainImageIndex) {
        newImageMainIndex = newImageIndex;
      }
    });

    // Send mainImageIndex only if a new image is selected as main
    if (newImageMainIndex >= 0) {
      formData.append("mainImageIndex", newImageMainIndex);
    }

    if (selectedProduct && deletedImageIds.length > 0) {
      formData.append("deletedImageIds", JSON.stringify(deletedImageIds));
    }

    // If main image is an existing image (not new), send its ID
    if (mainImageIndex >= 0 && mainImageIndex < images.length) {
      const selectedMainImage = images[mainImageIndex];
      if (
        selectedMainImage &&
        !selectedMainImage.isNew &&
        selectedMainImage.id
      ) {
        formData.append("existingMainImageId", selectedMainImage.id);
      }
    }

    // Add text data
    formData.append("name", productName);
    formData.append("description", productDescription);
    formData.append("price", productPrice);
    if (productSalePrice) {
      formData.append("salePrice", productSalePrice);
    }

    formData.append("bandId", user.bandId);
    formData.append("categoryId", selectedCategory);

    formData.append("isNew", isNew ? "true" : "false");
    formData.append("isFeatured", isFeatured ? "true" : "false");
    formData.append("isLimited", isLimited ? "true" : "false");
    formData.append("isBestSeller", isBestSeller ? "true" : "false");

    // Add sizes
    formData.append("sizeS", productSizes.includes("S") ? "true" : "false");
    formData.append("sizeM", productSizes.includes("M") ? "true" : "false");
    formData.append("sizeL", productSizes.includes("L") ? "true" : "false");
    formData.append("sizeXL", productSizes.includes("XL") ? "true" : "false");
    formData.append("sizeXXL", productSizes.includes("XXL") ? "true" : "false");
    formData.append("size3XL", productSizes.includes("3XL") ? "true" : "false");
    formData.append("size4XL", productSizes.includes("4XL") ? "true" : "false");
    formData.append("size5XL", productSizes.includes("5XL") ? "true" : "false");

    // Add stock po veličinama
    formData.append(
      "stockSizeS",
      productSizes.includes("S") ? sizeStocks.S.toString() : "0"
    );
    formData.append(
      "stockSizeM",
      productSizes.includes("M") ? sizeStocks.M.toString() : "0"
    );
    formData.append(
      "stockSizeL",
      productSizes.includes("L") ? sizeStocks.L.toString() : "0"
    );
    formData.append(
      "stockSizeXL",
      productSizes.includes("XL") ? sizeStocks.XL.toString() : "0"
    );
    formData.append(
      "stockSizeXXL",
      productSizes.includes("XXL") ? sizeStocks.XXL.toString() : "0"
    );
    formData.append(
      "stockSize3XL",
      productSizes.includes("3XL") ? sizeStocks["3XL"].toString() : "0"
    );
    formData.append(
      "stockSize4XL",
      productSizes.includes("4XL") ? sizeStocks["4XL"].toString() : "0"
    );
    formData.append(
      "stockSize5XL",
      productSizes.includes("5XL") ? sizeStocks["5XL"].toString() : "0"
    );

    // Add stock alert thresholds
    formData.append(
      "stockAlertS",
      productSizes.includes("S") ? sizeStockAlerts.S.toString() : "10"
    );
    formData.append(
      "stockAlertM",
      productSizes.includes("M") ? sizeStockAlerts.M.toString() : "10"
    );
    formData.append(
      "stockAlertL",
      productSizes.includes("L") ? sizeStockAlerts.L.toString() : "10"
    );
    formData.append(
      "stockAlertXL",
      productSizes.includes("XL") ? sizeStockAlerts.XL.toString() : "10"
    );
    formData.append(
      "stockAlertXXL",
      productSizes.includes("XXL") ? sizeStockAlerts.XXL.toString() : "10"
    );
    formData.append(
      "stockAlert3XL",
      productSizes.includes("3XL") ? sizeStockAlerts["3XL"].toString() : "10"
    );
    formData.append(
      "stockAlert4XL",
      productSizes.includes("4XL") ? sizeStockAlerts["4XL"].toString() : "10"
    );
    formData.append(
      "stockAlert5XL",
      productSizes.includes("5XL") ? sizeStockAlerts["5XL"].toString() : "10"
    );

    if (selectedProduct) {
      dispatch(
        editProduct({
          id: selectedProduct.id,
          requestData: formData,
        })
      )
        .unwrap()
        .then(() => {
          toast.success("Uspešno ste izmenili proizvod.");
          dispatch(getBandProducts({ bandId: user.bandId })).unwrap();
          closeModal();
        })
        .catch((err) => {
          toast.error(err.message);
        });
    } else {
      dispatch(createProduct(formData))
        .unwrap()
        .then(() => {
          toast.success("Uspešno ste kreirali proizvod.");
          dispatch(getBandProducts({ bandId: user.bandId })).unwrap();
          closeModal();
        })
        .catch((err) => {
          toast.error(err.message);
        });
    }
  };

  // Helper functions for product editing
  const sizesData = ["S", "M", "L", "XL", "XXL", "3XL", "4XL", "5XL"];

  // Handle size selection
  const toggleSize = (size) => {
    setSelectedSizes((prev) => {
      const newSizes = prev.includes(size)
        ? prev.filter((s) => s !== size)
        : [...prev, size];

      // Ako se veličina uklanja, resetuj njen stock
      if (!newSizes.includes(size)) {
        setSizeStocks((prevStocks) => ({
          ...prevStocks,
          [size]: 0,
        }));
      }

      return newSizes;
    });
  };

  // Handle size stock change
  const handleSizeStockChange = (size, value) => {
    setSizeStocks((prev) => ({
      ...prev,
      [size]: parseInt(value) || 0,
    }));
  };

  // Handle size stock alert change
  const handleSizeStockAlertChange = (size, value) => {
    setSizeStockAlerts((prev) => ({
      ...prev,
      [size]: parseInt(value) || 10,
    }));
  };

  // Kalkuliraj ukupni stock
  const calculateTotalStock = () => {
    return selectedSizes.reduce((total, size) => {
      return total + (parseInt(sizeStocks[size]) || 0);
    }, 0);
  };

  const currentCategory = categories.data.find(
    (c) => c.id === parseInt(selectedCategory)
  );

  useEffect(() => {
    if (!currentCategory?.hasSizes) {
      setSelectedSizes([]);
      setSizeStocks({
        S: 0,
        M: 0,
        L: 0,
        XL: 0,
        XXL: 0,
        "3XL": 0,
        "4XL": 0,
        "5XL": 0,
      });
    }
  }, [currentCategory]);

  const handleExclusiveCheck = (type) => {
    switch (type) {
      case "new":
        setIsNew(!isNew);
        break;
      case "featured":
        setIsFeatured(!isFeatured);
        break;
      case "bestseller":
        setIsBestSeller(!isBestSeller);
        break;
      case "limited":
        setIsLimited(!isLimited);
        break;
      default:
        break;
    }
  };

  // Image handling functions (ostaju isti...)
  const addImages = (files) => {
    if (images.length + files.length > 10) {
      toast.warning("Možete dodati maksimalno 10 slika");
      return;
    }

    const newImages = files.map((file, index) => ({
      id: `new_${Date.now()}_${index}`,
      file,
      url: URL.createObjectURL(file),
      isNew: true,
    }));

    setImages((prev) => [...prev, ...newImages]);
  };

  // Image handling functions
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith("image/")
    );

    if (files.length > 0) {
      addImages(files);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    addImages(files);
    e.target.value = "";
  };

  const removeImage = (index) => {
    const imageToRemove = images[index];

    if (imageToRemove.isExisting) {
      setDeletedImageIds((prev) => [...prev, imageToRemove.id]);
    }

    if (imageToRemove.isNew && imageToRemove.url) {
      URL.revokeObjectURL(imageToRemove.url);
    }

    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);

    if (mainImageIndex >= newImages.length) {
      setMainImageIndex(Math.max(0, newImages.length - 1));
    } else if (index < mainImageIndex) {
      setMainImageIndex(mainImageIndex - 1);
    } else if (index === mainImageIndex && newImages.length > 0) {
      setMainImageIndex(0);
    }
  };

  const setMainImage = (index) => {
    setMainImageIndex(index);
  };

  // Check if band has editing privileges
  const canEditProducts = () => {
    const analytics = bandAnalytics.data || {};
    const subscriptionPackage = analytics.subscriptionPackage;
    return (
      subscriptionPackage === "Basic 1" || subscriptionPackage === "Basic 2"
    );
  };

  return (
    <BandLayout>
      <PageSection $active>
        <TableHeader>
          <TableTitle>Moji Proizvodi</TableTitle>
          {canEditProducts() && (
            <Btn className="primary sm" onClick={openAddModal}>
              <FiPlus style={{ marginRight: "0.5rem" }} />
              Dodaj Proizvod
            </Btn>
          )}
        </TableHeader>
        {bandProducts.loading ? (
          <LoadingContainer>
            <l-line-spinner size="32" stroke="2.5" speed="1" color="#ff6b35" />
            Učitavanje proizvoda...
          </LoadingContainer>
        ) : (bandProducts.data || [])?.length === 0 ? (
          <LoadingContainer>Nemate proizvode...</LoadingContainer>
        ) : (
          <ProductGrid>
            {(bandProducts?.data || []).map((product, index) => {
              const productAnalytics = (
                bandProductAnalytics.data?.products || []
              ).find((p) => p.productId === product.id);

              return (
                <ProductCard key={product.id}>
                  <ProductImageContainer>
                    {getVariant(product) && (
                      <Badge $variant={getVariant(product)}>
                        {getBadgeLabel(product)}
                      </Badge>
                    )}
                    <ProductImage
                      src={
                        product.mainImage
                          ? product.mainImage
                          : "/placeholder.jpg"
                      }
                      alt={product.name}
                    />
                  </ProductImageContainer>

                  <ProductHeader>
                    <ProductTitle>{product.name}</ProductTitle>
                    <ProductBand>
                      {product.band?.name || "Unknown Band"}
                    </ProductBand>
                  </ProductHeader>

                  <ProductMeta>
                    <ProductMetaItem>
                      <ProductCategory>
                        {product.category?.name || "No Category"}
                      </ProductCategory>
                    </ProductMetaItem>

                    <ProductMetaItem>
                      <PriceContainer>
                        {product.salePrice ? (
                          <>
                            <ProductDiscountPrice>
                              {formatPrice(product.salePrice)}
                            </ProductDiscountPrice>
                            <ProductPrice $discounted>
                              {formatPrice(product.price)}
                            </ProductPrice>
                          </>
                        ) : (
                          <ProductPrice>
                            {formatPrice(product.price)}
                          </ProductPrice>
                        )}
                      </PriceContainer>
                    </ProductMetaItem>
                  </ProductMeta>

                  <StockInfo>
                    <StockBadge $inStock={product.stockQuantity > 0}>
                      Ukupno na stanju: {product.stockQuantity || 0}
                    </StockBadge>
                  </StockInfo>

                  {getProductSizeStocksWithAlerts(product).length > 0 && (
                    <SizeStockDisplay>
                      <SizeStockTitle>Stock po veličinama:</SizeStockTitle>
                      <SizeStockList>
                        {getProductSizeStocksWithAlerts(product).map(
                          (stockData, index) => {
                            return (
                              <SizeStockBadge
                                key={index}
                                $alertLevel={stockData.alertLevel}
                              >
                                {stockData.displayText}
                              </SizeStockBadge>
                            );
                          }
                        )}
                      </SizeStockList>
                    </SizeStockDisplay>
                  )}

                  {getProductSizes(product)?.length > 0 && (
                    <ProductSizes>
                      Veličine: {getProductSizes(product).join(", ")}
                    </ProductSizes>
                  )}

                  {/* Analytics Info - New section for band dashboard */}
                  {productAnalytics && (
                    <AnalyticsInfo>
                      <AnalyticsTitle>Analitika Prodaje</AnalyticsTitle>
                      <AnalyticsStats>
                        <AnalyticsStat>
                          Porudžbine: <span>{productAnalytics.orderCount}</span>
                        </AnalyticsStat>
                        <AnalyticsStat>
                          Prihod:{" "}
                          <span>
                            {formatPrice(productAnalytics.totalRevenue)}
                          </span>
                        </AnalyticsStat>
                        <AnalyticsStat>
                          Prodato:{" "}
                          <span>{productAnalytics.totalQuantitySold}</span>
                        </AnalyticsStat>
                      </AnalyticsStats>
                    </AnalyticsInfo>
                  )}

                  <div>
                    <ProductShortDesc>{product.description}</ProductShortDesc>
                  </div>

                  {canEditProducts() && (
                    <ProductActions style={{ marginTop: "1rem" }}>
                      <Btn
                        className="secondary sm"
                        onClick={() => openEditModal(product)}
                      >
                        <FiEdit />
                        Izmeni
                      </Btn>
                      <Button
                        $variant="danger"
                        onClick={() => {
                          setProductToDelete(product);
                          setDeleteProductModal(true);
                        }}
                      >
                        <FiTrash2 />
                        Obriši
                      </Button>
                    </ProductActions>
                  )}
                </ProductCard>
              );
            })}
          </ProductGrid>
        )}
      </PageSection>

      {/* Add/Edit Product Modal */}
      <Modal
        $active={activeModal === "addProduct" || activeModal === "editProduct"}
      >
        <ModalContent style={{ maxWidth: "700px", width: "95%" }}>
          <ModalHeader>
            <ModalTitle>
              {activeModal === "editProduct"
                ? "Izmeni Proizvod"
                : "Dodaj Novi Proizvod"}
            </ModalTitle>
            <ModalClose onClick={closeModal}>
              <FiX />
            </ModalClose>
          </ModalHeader>

          <div>
            {/* Images Upload Section */}
            <FormGroup>
              <FormLabel>Slike Proizvoda (maksimalno 10)</FormLabel>

              <ImageUploadArea
                $dragActive={dragActive}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById("imageInput").click()}
              >
                <FiUpload size={24} color="#ff6b35" />
                <p style={{ margin: "0.5rem 0", color: "#fff" }}>
                  Kliknite ili prevucite slike ovde
                </p>
                <p style={{ margin: 0, color: "#999", fontSize: "0.9rem" }}>
                  PNG, JPG do 5MB po slici
                </p>

                <input
                  id="imageInput"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: "none" }}
                />
              </ImageUploadArea>

              {images.length > 0 && (
                <ImagePreviewGrid>
                  {images.map((image, index) => (
                    <ImagePreviewItem
                      key={image.id}
                      $isMain={index === mainImageIndex}
                    >
                      <PreviewImage
                        src={image.url}
                        alt={`Preview ${index + 1}`}
                      />
                      <ImageControls>
                        {index !== mainImageIndex && (
                          <ImageControlButton
                            $primary
                            onClick={() => setMainImage(index)}
                            title="Postaviti kao glavnu sliku"
                          >
                            <FiStar />
                          </ImageControlButton>
                        )}
                        <ImageControlButton
                          onClick={() => removeImage(index)}
                          title="Ukloni sliku"
                        >
                          <FiX />
                        </ImageControlButton>
                      </ImageControls>
                      <ImageLabel>
                        {index === mainImageIndex ? "Glavna" : `${index + 1}`}
                        {image.isExisting && " (postojeća)"}
                      </ImageLabel>
                    </ImagePreviewItem>
                  ))}
                </ImagePreviewGrid>
              )}

              <UploadInstructions>
                <strong>Napomene:</strong> Prva slika ili označena slika će biti
                glavna slika proizvoda. Slike će biti automatski konvertovane u
                WebP format i promenjene na 500x500px. Maksimalno{" "}
                {10 - images.length} slika možete još dodati.
              </UploadInstructions>
            </FormGroup>

            {/* Basic Info */}
            <FormGroup>
              <FormLabel>Naziv Proizvoda</FormLabel>
              <FormInput
                type="text"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="Naziv proizvoda..."
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Kategorija</FormLabel>
              <Relative>
                <FormSelect
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  required
                >
                  <option value="">Izaberite kategoriju...</option>
                  {categories.data.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </FormSelect>
                <FiChevronDown
                  color="#ff6b35"
                  size={20}
                  style={{
                    position: "absolute",
                    right: 15,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              </Relative>
            </FormGroup>

            {/* Sizes and Stock */}
            {currentCategory?.hasSizes && (
              <FormGroup>
                <FormLabel>Dostupne Veličine</FormLabel>
                <SizeSelector>
                  {sizesData.map((size) => (
                    <SizeOption
                      key={size}
                      type="button"
                      $selected={selectedSizes.includes(size)}
                      onClick={() => toggleSize(size)}
                    >
                      {size}
                    </SizeOption>
                  ))}
                </SizeSelector>

                {/* Stock po veličinama */}
                {selectedSizes.length > 0 && (
                  <>
                    <FormLabel
                      style={{ marginTop: "1rem", marginBottom: "0.5rem" }}
                    >
                      Stock po veličinama
                    </FormLabel>
                    <SizeStockContainer>
                      {selectedSizes.map((size) => (
                        <SizeStockItem key={size}>
                          <SizeStockLabel>{size}</SizeStockLabel>
                          <SizeStockInput
                            type="number"
                            min="0"
                            value={sizeStocks[size]}
                            onChange={(e) =>
                              handleSizeStockChange(size, e.target.value)
                            }
                            placeholder="0"
                          />
                        </SizeStockItem>
                      ))}
                    </SizeStockContainer>

                    <FormLabel
                      style={{ marginTop: "1.5rem", marginBottom: "0.5rem" }}
                    >
                      Stock Alert Pragovi (kada da prime obaveštenje)
                    </FormLabel>
                    <SizeStockContainer>
                      {selectedSizes.map((size) => (
                        <SizeStockItem key={`alert-${size}`}>
                          <SizeStockLabel>{size}</SizeStockLabel>
                          <SizeStockInput
                            type="number"
                            min="1"
                            value={sizeStockAlerts[size]}
                            onChange={(e) =>
                              handleSizeStockAlertChange(size, e.target.value)
                            }
                            placeholder="10"
                          />
                        </SizeStockItem>
                      ))}
                    </SizeStockContainer>

                    <TotalStockDisplay>
                      <div className="label">Ukupno na stanju:</div>
                      <div className="value">{calculateTotalStock()}</div>
                    </TotalStockDisplay>
                  </>
                )}
              </FormGroup>
            )}

            {/* Ako nema veličine, dodaj običan stock input */}
            {!currentCategory?.hasSizes && (
              <FormGroup>
                <FormLabel>Količina na Stanju</FormLabel>
                <FormInput
                  type="number"
                  value={sizeStocks.S} // Koristimo samo S za proizvode bez veličina
                  onChange={(e) => handleSizeStockChange("S", e.target.value)}
                  placeholder="50"
                  required
                />
              </FormGroup>
            )}

            {/* Pricing */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
              }}
            >
              <FormGroup>
                <FormLabel>Regularna Cena (RSD)</FormLabel>
                <FormInput
                  type="number"
                  value={productPrice}
                  onChange={(e) => setProductPrice(e.target.value)}
                  placeholder="2500"
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Cena sa Popustom (RSD) - opciono</FormLabel>
                <FormInput
                  type="number"
                  value={productSalePrice}
                  onChange={(e) => setProductSalePrice(e.target.value)}
                  placeholder="2000"
                />
              </FormGroup>
            </div>

            {/* Description */}
            <FormGroup>
              <FormLabel>Opis</FormLabel>
              <FormTextarea
                value={productDescription}
                onChange={(e) => setProductDescription(e.target.value)}
                placeholder="Detaljnan opis proizvoda, materijal, karakteristike..."
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Opcije</FormLabel>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
                <label
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: ".5rem",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isNew}
                    onChange={() => handleExclusiveCheck("new")}
                  />{" "}
                  Novo
                </label>
                <label
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: ".5rem",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isFeatured}
                    onChange={() => handleExclusiveCheck("featured")}
                  />{" "}
                  Istaknuto
                </label>
                <label
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: ".5rem",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isBestSeller}
                    onChange={() => handleExclusiveCheck("bestseller")}
                  />{" "}
                  Bestseller
                </label>
                <label
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: ".5rem",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isLimited}
                    onChange={() => handleExclusiveCheck("limited")}
                  />{" "}
                  Limitirano
                </label>
              </div>
            </FormGroup>

            <FormActionsContainer>
              <Button onClick={handleProductSubmit}>
                {activeModal === "editProduct"
                  ? "Sačuvaj Izmene"
                  : "Dodaj Proizvod"}
              </Button>
              <Button onClick={closeModal}>Otkaži</Button>
            </FormActionsContainer>
          </div>
        </ModalContent>
      </Modal>
      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteProductModal && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              if (!deleteLoading) {
                setDeleteProductModal(false);
              }
            }}
          >
            <ConfirmBox
              as={motion.div}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <CloseBtn
                onClick={() => {
                  if (!deleteLoading) {
                    setDeleteProductModal(false);
                  }
                }}
              >
                <FaTimes />
              </CloseBtn>
              <h3>Potvrdi brisanje</h3>
              <p>
                Da li si siguran da želiš da obrišeš proizvod "
                {productToDelete?.name}"?
              </p>
              <ModalActions>
                <Cancel
                  onClick={() => {
                    if (!deleteLoading) {
                      setDeleteProductModal(false);
                    }
                  }}
                >
                  Otkaži
                </Cancel>
                {!deleteLoading ? (
                  <Confirm onClick={handleDeleteProduct}>Obriši</Confirm>
                ) : (
                  <Confirm $disabled>Brišem</Confirm>
                )}
              </ModalActions>
            </ConfirmBox>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </BandLayout>
  );
};

export default BandProductsPage;
